import { graphql } from 'gql.tada';
import { useMutation } from '@apollo/client';
import { useLazyQuery } from '@apollo/client';

const CreateCourseGradebookMutation = graphql.persisted(
  "CreateCourseGradebook",
  graphql(`
    mutation CreateCourseGradebook($input: CreateCourseGradebookInput!) {
      createCourseGradebook(input: $input) {
        gradebooks {
          id
          name
          transcriptCourse { id name creditValue termCount }
        }
        userErrors { message path }
      }
    }
  `)
);

const UpdateCourseGradebookMutation = graphql.persisted(
  "UpdateCourseGradebook",
  graphql(`
    mutation UpdateCourseGradebook($input: UpdateCourseGradebookInput!) {
      updateCourseGradebook(input: $input) {
        transcriptCourse { id name creditValue termCount }
        userErrors { message path }
      }
    }
  `)
);

const DeleteCourseGradebookMutation = graphql.persisted(
  "DeleteCourseGradebook",
  graphql(`
    mutation DeleteCourseGradebook($input: DeleteCourseGradebookInput!) {
      deleteCourseGradebook(input: $input) {
        success
        userErrors { message path }
      }
    }
  `)
);

const GradingTemplatePreviewQuery = graphql.persisted(
  "GradingTemplatePreview",
  graphql(`
    query GradingTemplatePreview($gradebookSeriesId: ID!, $templateId: ID!) {
      gradingTemplatePreview(
        gradebookSeriesId: $gradebookSeriesId
        templateId: $templateId
      ) {
        changed
        outOfDate
        applicable
        digest
        conflicts
        additions { categoryId templateCategoryId name current { name weight dropLowest position } proposed { name weight dropLowest position } reason }
        updates { categoryId templateCategoryId name current { name weight dropLowest position } proposed { name weight dropLowest position } reason }
        removals { categoryId templateCategoryId name current { name weight dropLowest position } proposed { name weight dropLowest position } reason }
        retained { categoryId templateCategoryId name current { name weight dropLowest position } proposed { name weight dropLowest position } reason }
        unchanged { categoryId templateCategoryId name current { name weight dropLowest position } proposed { name weight dropLowest position } reason }
      }
    }
  `)
);

const ApplyGradingTemplateMutation = graphql.persisted(
  "ApplyGradingTemplate",
  graphql(`
    mutation ApplyGradingTemplate($input: ApplyGradingTemplateInput!) {
      applyGradingTemplate(input: $input) {
        transcriptCourse {
          id
          name
          gradingTemplate { id name }
          gradingTemplateOutOfDate
        }
        preview {
          additions { name }
          updates { name }
          removals { name }
          retained { name reason }
          unchanged { name }
          conflicts
        }
        userErrors { message path }
      }
    }
  `)
);

const UnlinkGradingTemplateMutation = graphql.persisted(
  "UnlinkGradingTemplate",
  graphql(`
    mutation UnlinkGradingTemplate($input: UnlinkGradingTemplateInput!) {
      unlinkGradingTemplate(input: $input) {
        transcriptCourse { id name gradingTemplate { id name } gradingTemplateOutOfDate }
        unlinkedCategoryCount
        userErrors { message path }
      }
    }
  `)
);

const SaveCustomAsGradingTemplateMutation = graphql.persisted(
  "SaveCustomAsGradingTemplate",
  graphql(`
    mutation SaveCustomAsGradingTemplate($input: SaveCustomAsGradingTemplateInput!) {
      saveCustomAsGradingTemplate(input: $input) {
        gradingTemplate {
          id
          name
          description
          weightTotal
          categories { id name weight dropLowest position }
        }
        userErrors { message path }
      }
    }
  `)
);

export {
  CreateCourseGradebookMutation,
  UpdateCourseGradebookMutation,
  DeleteCourseGradebookMutation,
  GradingTemplatePreviewQuery,
  ApplyGradingTemplateMutation,
  UnlinkGradingTemplateMutation,
  SaveCustomAsGradingTemplateMutation,
};

export function useCreateCourseGradebook() {
  return useMutation(CreateCourseGradebookMutation);
}

export function useUpdateCourseGradebook() {
  return useMutation(UpdateCourseGradebookMutation);
}

export function useDeleteCourseGradebook() {
  return useMutation(DeleteCourseGradebookMutation);
}

export function useGradingTemplatePreview() {
  return useLazyQuery(GradingTemplatePreviewQuery, { fetchPolicy: 'network-only' });
}

export function useApplyGradingTemplate() {
  return useMutation(ApplyGradingTemplateMutation);
}

export function useUnlinkGradingTemplate() {
  return useMutation(UnlinkGradingTemplateMutation);
}

export function useSaveCustomAsGradingTemplate() {
  return useMutation(SaveCustomAsGradingTemplateMutation);
}
